import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "./AuthContext";
import {
  requestNotificationPermission,
  onForegroundMessage,
  saveFCMTokenToBackend,
} from "../config/firebase";
import { toast } from "sonner";

const NotificationContext = createContext(null);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error("useNotifications must be used within a NotificationProvider");
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { token: authToken, isAuthenticated } = useAuth();
  const [fcmToken, setFcmToken] = useState(null);
  const [permission, setPermission] = useState(Notification.permission || "default");
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const tokenRegistrationAttempted = useRef(false);
  const prevAuthRef = useRef({ isAuthenticated: false, authToken: null });

  // Request notification permission and get FCM token
  const requestPermission = useCallback(async () => {
    if (!isAuthenticated) return;

    setLoading(true);
    try {
      const token = await requestNotificationPermission();
      if (token) {
        setFcmToken(token);
        setPermission("granted");
        localStorage.setItem("foundry-fcm-token", token);

        // Save token to backend
        if (authToken) {
          await saveFCMTokenToBackend(token, authToken);
        }

        toast.success("Notifications enabled successfully");
        return true;
      } else {
        setPermission(Notification.permission);
        if (Notification.permission === "denied") {
          toast.error("Notification permission denied. Please enable in browser settings.");
        }
        return false;
      }
    } catch (error) {
      console.error("Error requesting notification permission:", error);
      toast.error(`Failed to enable notifications: ${error.message}`);
      return false;
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, authToken]);

  // Fetch notifications from backend
  const fetchNotifications = useCallback(async () => {
    if (!authToken) return;

    const API_URL = process.env.REACT_APP_BACKEND_URL || "";
    try {
      const response = await fetch(`${API_URL}/api/notifications`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        // Deduplicate notifications based on id
        const fetchedNotifications = data.notifications || [];
        setNotifications((prev) => {
          // Create a map of existing notifications (prioritize fetched data)
          const notificationMap = new Map();
          fetchedNotifications.forEach((n) => notificationMap.set(n.id, n));
          // Add any local-only notifications that aren't in the fetched data
          prev.forEach((n) => {
            if (!notificationMap.has(n.id)) {
              notificationMap.set(n.id, n);
            }
          });
          // Sort by created_at descending
          return Array.from(notificationMap.values()).sort(
            (a, b) => new Date(b.created_at) - new Date(a.created_at)
          );
        });
        setUnreadCount(data.unread_count || 0);
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  }, [authToken]);

  // Mark notification as read
  const markAsRead = useCallback(async (notificationId) => {
    if (!authToken) return;

    const API_URL = process.env.REACT_APP_BACKEND_URL || "";
    try {
      const response = await fetch(`${API_URL}/api/notifications/${notificationId}/read`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });

      if (response.ok) {
        setNotifications((prev) =>
          prev.map((n) => (n.id === notificationId ? { ...n, read: true } : n))
        );
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  }, [authToken]);

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    if (!authToken) return;

    const API_URL = process.env.REACT_APP_BACKEND_URL || "";
    try {
      const response = await fetch(`${API_URL}/api/notifications/mark-all-read`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });

      if (response.ok) {
        setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
        setUnreadCount(0);
      }
    } catch (error) {
      console.error("Error marking all notifications as read:", error);
    }
  }, [authToken]);

  // Delete notification
  const deleteNotification = useCallback(async (notificationId) => {
    if (!authToken) return;

    const API_URL = process.env.REACT_APP_BACKEND_URL || "";
    try {
      const response = await fetch(`${API_URL}/api/notifications/${notificationId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });

      if (response.ok) {
        setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
        // Update unread count if deleted notification was unread
        const deletedNotification = notifications.find((n) => n.id === notificationId);
        if (deletedNotification && !deletedNotification.read) {
          setUnreadCount((prev) => Math.max(0, prev - 1));
        }
      }
    } catch (error) {
      console.error("Error deleting notification:", error);
    }
  }, [authToken, notifications]);

  // Clear all notifications
  const clearAllNotifications = useCallback(async () => {
    if (!authToken) return;

    const API_URL = process.env.REACT_APP_BACKEND_URL || "";
    try {
      const response = await fetch(`${API_URL}/api/notifications/clear-all`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });

      if (response.ok) {
        setNotifications([]);
        setUnreadCount(0);
        toast.success("All notifications cleared");
      }
    } catch (error) {
      console.error("Error clearing all notifications:", error);
      toast.error(`Failed to clear notifications: ${error.message}`);
    }
  }, [authToken]);

  // Handle incoming foreground messages
  useEffect(() => {
    if (!isAuthenticated) return;

    const unsubscribe = onForegroundMessage((payload) => {
      console.log("Foreground notification received:", payload);

      // Show toast notification
      const title = payload.notification?.title || payload.data?.title || "New Notification";
      const body = payload.notification?.body || payload.data?.body || "";

      toast.info(
        <div>
          <strong>{title}</strong>
          {body && <p className="text-sm mt-1">{body}</p>}
        </div>,
        {
          duration: 5000,
          action: {
            label: "View",
            onClick: () => {
              // Navigate to notifications or specific page
              if (payload.data?.url) {
                window.location.href = payload.data.url;
              }
            },
          },
        }
      );

      // Add to notifications list (avoid duplicates)
      const newNotification = {
        id: payload.messageId || `local-${Date.now()}`,
        title,
        body,
        data: payload.data,
        created_at: new Date().toISOString(),
        read: false,
      };

      setNotifications((prev) => {
        // Check if notification already exists
        if (prev.some((n) => n.id === newNotification.id)) {
          return prev;
        }
        return [newNotification, ...prev];
      });
      setUnreadCount((prev) => prev + 1);
    });

    return () => unsubscribe();
  }, [isAuthenticated]);

  // Fetch notifications when authenticated
  useEffect(() => {
    if (isAuthenticated && authToken) {
      fetchNotifications();
    }
  }, [isAuthenticated, authToken, fetchNotifications]);

  // Silent token registration when permission is already granted
  useEffect(() => {
    const registerTokenSilently = async () => {
      // Skip if not authenticated, no token, or already attempted
      if (!isAuthenticated || !authToken || tokenRegistrationAttempted.current) return;

      // Only proceed if permission is already granted
      if (Notification.permission === "granted") {
        tokenRegistrationAttempted.current = true;
        try {
          const token = await requestNotificationPermission();
          if (token) {
            setFcmToken(token);
            setPermission("granted");
            localStorage.setItem("foundry-fcm-token", token);
            await saveFCMTokenToBackend(token, authToken);
            console.log("FCM token silently registered");
          }
        } catch (error) {
          console.error("Silent token registration failed:", error);
          // Reset so it can be retried
          tokenRegistrationAttempted.current = false;
        }
      }
    };

    registerTokenSilently();
  }, [isAuthenticated, authToken]);

  // Register service worker
  useEffect(() => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker
        .register("/firebase-messaging-sw.js")
        .then((registration) => {
          console.log("Service Worker registered:", registration);
        })
        .catch((error) => {
          console.error("Service Worker registration failed:", error);
        });
    }
  }, []);

  // Detect logout: unregister device and clear notification state
  useEffect(() => {
    const prev = prevAuthRef.current;

    // Was authenticated, now not → user logged out
    if (prev.isAuthenticated && !isAuthenticated) {
      const API_URL = process.env.REACT_APP_BACKEND_URL || "";

      // Unregister device from backend using the PREVIOUS auth token (still valid JWT)
      if (prev.authToken) {
        fetch(`${API_URL}/api/notifications/devices/unregister`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${prev.authToken}`,
          },
        }).catch(() => {
          // Silently ignore - token may have expired
        });
      }

      // Clear all notification state
      setFcmToken(null);
      setNotifications([]);
      setUnreadCount(0);
      localStorage.removeItem("foundry-fcm-token");
      tokenRegistrationAttempted.current = false;
      console.log("[Notifications] Device unregistered and state cleared on logout");
    }

    // Update ref for next render
    prevAuthRef.current = { isAuthenticated, authToken };
  }, [isAuthenticated, authToken]);

  const value = {
    fcmToken,
    permission,
    notifications,
    unreadCount,
    loading,
    requestPermission,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    clearAllNotifications,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationContext;
